import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ChannelName } from '../../types';
import { T_KEYS } from '../../constants';

// Tutorial image paths - using public folder paths
const TUTORIAL_IMAGES = {
  airbnb: [
    '/images/Tutorial/AirBnb/1.png',
    '/images/Tutorial/AirBnb/1-.png',
    '/images/Tutorial/AirBnb/3.png',
  ],
  gathern: [
    '/images/Tutorial/Gathren/1.png',
    '/images/Tutorial/Gathren/2.png',
    '/images/Tutorial/Gathren/3.png',
  ],
  booking: [
    '/images/Tutorial/Booking/1.png',
    '/images/Tutorial/Booking/2.png',
    '/images/Tutorial/Booking/3.png',
    '/images/Tutorial/Booking/4.png',
    '/images/Tutorial/Booking/5.png',
    '/images/Tutorial/Booking/6.png',
  ],
};

interface TutorialModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  channelName: ChannelName;
}

interface TutorialStep {
  titleKey: string;
  descriptionKey: string;
}

// Tutorial steps for each channel - images are loaded from TUTORIAL_IMAGES by index
const CHANNEL_TUTORIAL_STEPS: Record<ChannelName, TutorialStep[]> = {
  airbnb: [
    { titleKey: T_KEYS.TUTORIAL_AIRBNB_STEP1_TITLE, descriptionKey: T_KEYS.TUTORIAL_AIRBNB_STEP1_DESC },
    { titleKey: T_KEYS.TUTORIAL_AIRBNB_STEP2_TITLE, descriptionKey: T_KEYS.TUTORIAL_AIRBNB_STEP2_DESC },
    { titleKey: T_KEYS.TUTORIAL_AIRBNB_STEP3_TITLE, descriptionKey: T_KEYS.TUTORIAL_AIRBNB_STEP3_DESC },
  ],
  gathern: [
    { titleKey: T_KEYS.TUTORIAL_GATHERN_STEP1_TITLE, descriptionKey: T_KEYS.TUTORIAL_GATHERN_STEP1_DESC },
    { titleKey: T_KEYS.TUTORIAL_GATHERN_STEP2_TITLE, descriptionKey: T_KEYS.TUTORIAL_GATHERN_STEP2_DESC },
    { titleKey: T_KEYS.TUTORIAL_GATHERN_STEP3_TITLE, descriptionKey: T_KEYS.TUTORIAL_GATHERN_STEP3_DESC },
  ],
  booking: [
    { titleKey: T_KEYS.TUTORIAL_BOOKING_STEP1_TITLE, descriptionKey: T_KEYS.TUTORIAL_BOOKING_STEP1_DESC },
    { titleKey: T_KEYS.TUTORIAL_BOOKING_STEP2_TITLE, descriptionKey: T_KEYS.TUTORIAL_BOOKING_STEP2_DESC },
    { titleKey: T_KEYS.TUTORIAL_BOOKING_STEP3_TITLE, descriptionKey: T_KEYS.TUTORIAL_BOOKING_STEP3_DESC },
    { titleKey: T_KEYS.TUTORIAL_BOOKING_STEP4_TITLE, descriptionKey: T_KEYS.TUTORIAL_BOOKING_STEP4_DESC },
    { titleKey: T_KEYS.TUTORIAL_BOOKING_STEP5_TITLE, descriptionKey: T_KEYS.TUTORIAL_BOOKING_STEP5_DESC },
    { titleKey: T_KEYS.TUTORIAL_BOOKING_STEP6_TITLE, descriptionKey: T_KEYS.TUTORIAL_BOOKING_STEP6_DESC },
  ],
};

const TutorialModal: FC<TutorialModalProps> = ({
  open,
  onClose,
  onComplete,
  channelName,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [currentStep, setCurrentStep] = useState(0);

  const steps = CHANNEL_TUTORIAL_STEPS[channelName] || CHANNEL_TUTORIAL_STEPS.airbnb;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;
  const progress = ((currentStep + 1) / steps.length) * 100;
  
  // Get image for current step from TUTORIAL_IMAGES
  const currentImage = TUTORIAL_IMAGES[channelName]?.[currentStep] || '';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="tutorial-modal"
    >
      <ModalContainer>
        <CloseButton onClick={handleClose}>
          <CloseIcon />
        </CloseButton>

        <ModalContent>
          <StepTitle>
            {t(step.titleKey)}
          </StepTitle>

          <ImageContainer>
            <TutorialImage src={currentImage} alt={t(step.titleKey)} />
          </ImageContainer>

          <StepDescription>
            {t(step.descriptionKey)}
          </StepDescription>
        </ModalContent>

        <ProgressContainer>
          <ProgressBar>
            <ProgressFill style={{ width: `${progress}%` }} />
          </ProgressBar>

          <ButtonContainer $isFirstStep={isFirstStep}>
            {!isFirstStep && (
              <SecondaryButton onClick={handlePrev}>
                {isRtl ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                {t(T_KEYS.TUTORIAL_PREVIOUS)}
              </SecondaryButton>
            )}

            <PrimaryButton $isLastStep={isLastStep} onClick={handleNext}>
              {isLastStep ? (
                t(T_KEYS.TUTORIAL_START_CONNECTION)
              ) : (
                <>
                  {t(T_KEYS.TUTORIAL_NEXT)}
                  {isRtl ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </>
              )}
            </PrimaryButton>
          </ButtonContainer>
        </ProgressContainer>
      </ModalContainer>
    </Modal>
  );
};

// Styled Components
const ModalContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 650px;
  background: white;
  border-radius: 24px;
  padding: 32px;
  outline: none;
`;

const CloseButton = styled(IconButton)`
  position: absolute !important;
  top: 16px;
  inset-inline-end: 16px;
  color: #666 !important;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
`;

const StepTitle = styled.h2`
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-weight: 600;
  font-size: 28px;
  line-height: 36px;
  color: #262626;
  margin: 0;
  text-align: center;
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 400px;
  background: #f7f7f7;
  border: 1px solid #ebebeb;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const TutorialImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const StepDescription = styled.p`
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 30px;
  color: #666666;
  margin: 0;
  text-align: center;
`;

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #ebebeb;
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #5b2ff9 0%, #7c5cff 100%);
  border-radius: 2px;
  transition: width 0.3s ease;
`;

const ButtonContainer = styled.div<{ $isFirstStep: boolean }>`
  display: flex;
  justify-content: ${({ $isFirstStep }) => ($isFirstStep ? 'flex-end' : 'space-between')};
  gap: 16px;
`;

const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #f7f7f7;
  border: none;
  border-radius: 12px;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #666666;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #ebebeb;
  }

  svg {
    font-size: 20px;
  }
`;

const PrimaryButton = styled.button<{ $isLastStep: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 32px;
  background: ${({ $isLastStep }) =>
    $isLastStep
      ? 'linear-gradient(90deg, #5b2ff9 0%, #7c5cff 100%)'
      : '#5b2ff9'};
  border: none;
  border-radius: 12px;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: white;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  svg {
    font-size: 20px;
  }
`;

export default TutorialModal;
